// src/data/studiesHelpers.js
//
// Thin wrappers around the studies/{studyId} collection introduced by
// ticket 03 and surfaced in the admin UI by ticket 13.

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

const STUDIES = 'studies';

/**
 * Returns every non-archived study in an org, sorted by name.
 */
export async function loadStudiesForOrg(orgId) {
  if (!orgId) return [];
  const q = query(collection(db, STUDIES), where('orgId', '==', orgId));
  const snap = await getDocs(q);
  const out = [];
  snap.forEach((d) => {
    const data = d.data();
    if (!data.archived) out.push({ id: d.id, ...data });
  });
  out.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return out;
}

/**
 * Returns every study in an org, including archived ones. Used by the
 * admin studies list when the "Show archived" toggle is on.
 */
export async function loadAllStudiesForOrg(orgId) {
  if (!orgId) return [];
  const q = query(collection(db, STUDIES), where('orgId', '==', orgId));
  const snap = await getDocs(q);
  const out = [];
  snap.forEach((d) => out.push({ id: d.id, ...d.data() }));
  out.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return out;
}

/**
 * Fetches a single study by id.
 */
export async function getStudy(studyId) {
  if (!studyId) return null;
  const snap = await getDoc(doc(db, STUDIES, studyId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Creates a new study. Returns the generated studyId.
 */
export async function createStudy({ orgId, name, description = '', createdBy }) {
  if (!orgId || !name) throw new Error('createStudy requires orgId and name');
  const now = serverTimestamp();
  const ref = await addDoc(collection(db, STUDIES), {
    orgId,
    name,
    description,
    archived: false,
    createdBy: createdBy || 'unknown',
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

/**
 * Renames a study. orgId and createdBy are immutable; the rules enforce.
 */
export async function renameStudy(studyId, { name, description }) {
  if (!studyId) throw new Error('renameStudy requires studyId');
  const patch = { updatedAt: serverTimestamp() };
  if (name !== undefined) patch.name = name;
  if (description !== undefined) patch.description = description;
  await setDoc(doc(db, STUDIES, studyId), patch, { merge: true });
}

/**
 * Archives a study (soft — the doc stays, archived:true hides it
 * from default admin views and reviewer sidebars). Reverse with
 * unarchiveStudy.
 */
export async function archiveStudy(studyId) {
  if (!studyId) throw new Error('archiveStudy requires studyId');
  await setDoc(
    doc(db, STUDIES, studyId),
    { archived: true, updatedAt: serverTimestamp() },
    { merge: true },
  );
}

export async function unarchiveStudy(studyId) {
  if (!studyId) throw new Error('unarchiveStudy requires studyId');
  await setDoc(
    doc(db, STUDIES, studyId),
    { archived: false, updatedAt: serverTimestamp() },
    { merge: true },
  );
}

/** Hard delete. Use sparingly — only for admin cleanups. */
export async function deleteStudy(studyId) {
  if (!studyId) throw new Error('deleteStudy requires studyId');
  await deleteDoc(doc(db, STUDIES, studyId));
}
